import { EvoluaApiError } from "@evolua/api-client";
import {
  Alert,
  Button,
  EmptyState,
  ErrorState,
  Field,
  Inline,
  Modal,
  PageHeader,
  Skeleton,
  Table,
  StatusBadge,
} from "@evolua/ui";
import { useState } from "react";
import {
  useCreateHrAdmission,
  useHandoffHrAdmission,
  useHrAdmission,
  useHrAdmissions,
  useValidateHrAdmissionItem,
} from "../api/use-hr.js";

const COMPANY_ID = "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa";

/** Admissão digital — RH (FASE-06-T53). */
export function AdmissoesRhPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);
  const [rejectKey, setRejectKey] = useState<string | null>(null);

  const list = useHrAdmissions();
  const detail = useHrAdmission(selectedId);
  const create = useCreateHrAdmission();
  const validate = useValidateHrAdmissionItem();
  const handoff = useHandoffHrAdmission();

  const correlationId =
    list.error instanceof EvoluaApiError ? list.error.problem.traceId : undefined;

  async function onCreate() {
    setActionError(null);
    if (!name.trim() || !email.trim()) {
      setActionError("Informe nome e e-mail do candidato.");
      return;
    }
    try {
      const created = await create.mutateAsync({
        companyId: COMPANY_ID,
        candidateName: name.trim(),
        candidateEmail: email.trim(),
      });
      setName("");
      setEmail("");
      setSelectedId(created.id);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha ao criar");
    }
  }

  async function onValidate(key: string, decision: "VALIDATED" | "REJECTED") {
    if (!selectedId) return;
    setActionError(null);
    try {
      await validate.mutateAsync({ id: selectedId, key, decision });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha na validação");
    }
  }

  async function onHandoff() {
    if (!selectedId) return;
    setActionError(null);
    try {
      await handoff.mutateAsync(selectedId);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Handoff bloqueado");
    }
  }

  if (list.isPending) return <Skeleton height="8rem" label="Admissões" />;
  if (list.isError || !list.data) {
    return (
      <ErrorState
        {...(correlationId ? { correlationId } : {})}
        onRetry={() => void list.refetch()}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Admissões (RH)"
        lead="Checklist configurável, link seguro do candidato, validação e handoff mock ao ERP."
      />

      <form
        className="evolua-stack evolua-stack--3"
        onSubmit={(e) => {
          e.preventDefault();
          void onCreate();
        }}
      >
        <Field
          label="Nome do candidato"
          type="text"
          placeholder="Nome do candidato"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-label="Nome do candidato"
        />
        <Field
          label="E-mail"
          type="email"
          placeholder="E-mail"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-label="E-mail do candidato"
        />
        <Button type="submit" size="sm" disabled={create.isPending}>
          {create.isPending ? "Criando…" : "Abrir admissão + convite"}
        </Button>
      </form>

      {actionError ? <Alert tone="danger">{actionError}</Alert> : null}

      {list.data.data.length === 0 ? (
        <EmptyState title="Nenhuma admissão" description="Crie a primeira acima." />
      ) : (
        <Table
          caption="Admissões"
          getRowId={(item) => item.id}
          columns={[
            { id: "name", header: "Candidato", cell: (item) => item.candidateName },
            {
              id: "status",
              header: "Status",
              cell: (item) => <StatusBadge status={item.status} />,
            },
            {
              id: "progress",
              header: "Progresso",
              align: "numeric",
              cell: (item) => `${item.progressPercent}%`,
            },
            {
              id: "actions",
              header: "Ações",
              cell: (item) => (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedId(item.id)}
                >
                  Abrir
                </Button>
              ),
            },
          ]}
          rows={list.data.data}
        />
      )}

      {selectedId && detail.data ? (
        <section className="evolua-stack evolua-stack--3">
          <h2 className="evolua-section-title">{detail.data.candidateName}</h2>
          <p className="evolua-page-lead">
            Link do candidato:{" "}
            <a href={detail.data.invitePath}>{detail.data.invitePath}</a>
          </p>
          <Table
            caption="Checklist"
            getRowId={(item) => item.key}
            columns={[
              { id: "label", header: "Item", cell: (item) => item.label },
              {
                id: "status",
                header: "Status",
                cell: (item) => <StatusBadge status={item.status} />,
              },
              {
                id: "actions",
                header: "Ações",
                cell: (item) =>
                  item.status === "SUBMITTED" ? (
                    <Inline>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => void onValidate(item.key, "VALIDATED")}
                      >
                        Validar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => setRejectKey(item.key)}
                      >
                        Rejeitar
                      </Button>
                    </Inline>
                  ) : (
                    "—"
                  ),
              },
            ]}
            rows={detail.data.checklist}
          />
          <Button
            type="button"
            disabled={
              handoff.isPending ||
              (detail.data.status !== "READY_FOR_HANDOFF" &&
                detail.data.status !== "IN_REVIEW")
            }
            onClick={() => void onHandoff()}
          >
            {handoff.isPending ? "Enviando…" : "Handoff → ERP"}
          </Button>
          {detail.data.erpHandoffRef ? (
            <p className="evolua-muted">
              Ref. ERP: <code>{detail.data.erpHandoffRef}</code>
            </p>
          ) : null}
        </section>
      ) : null}

      <Modal
        open={Boolean(rejectKey)}
        title="Rejeitar item"
        onClose={() => setRejectKey(null)}
        size="sm"
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setRejectKey(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={() => {
                if (!rejectKey) return;
                const key = rejectKey;
                setRejectKey(null);
                void onValidate(key, "REJECTED");
              }}
            >
              Rejeitar
            </Button>
          </>
        }
      >
        <p>O item volta para o candidato. Confirma rejeitar?</p>
      </Modal>
    </>
  );
}
